// Client-side entry point for external integration operations.
//
// Governance-owned integrations (kind='supabase_project', 'github',
// 'google_workspace', 'npm', etc.) live in the `org_integrations` table
// of the governance Supabase project. Their secrets live in Vault.
// Neither secrets nor service-role keys can ever reach the browser —
// all ops have to round-trip through a server-side edge function.
//
// `callIntegrationOp()` is that round trip. It:
//   1. Reads the active user's governance JWT from the Supabase client.
//   2. POSTs the op body to `external-supabase-query` with that JWT.
//   3. Returns the parsed JSON response, whose shape is
//      `{ ok: true, op, result }` on success, `{ error: string }` on
//      failure.
//
// The edge function re-checks org membership from the JWT's
// `app_metadata.orgs` claim. It will 403 if the caller isn't in the
// org, 404 if the integration isn't configured, 500 on upstream errors.
//
// The function name "external-supabase-query" is historic — it handles
// not only Supabase CRUD but also `list_projects` and
// `list_integrations` which are governance-local. Everything else
// resolves a per-(org,kind,slug) external target before executing.
//
// Admin ops (writing integration config/secret, deleting) go through
// the `integration-admin` edge function via the private `_adminCall`
// helper below — same JWT auth, but the edge function additionally
// verifies admin/owner membership before touching Vault.

import { apiUrl as channelApiUrl } from '@symbo.ls/channels'
import { getClient, PROJECT_GOVERNANCE } from '../governanceBridge.js'
import { slugify } from '../functions/slugify.js'

// ---------- Mongo (Symbols server) cutover flag ----------
//
// Env var: SYMBOLS_APP_USE_MONGO_INTEGRATIONS
//
// Feature flag: when set to "1" / "true" / "yes" / "on" (case-insensitive),
// every admin / read / capability call is routed to the Symbols server's
// /org-integrations/* REST endpoints (Mongo-backed) with the user's
// governance JWT as a bearer. When unset or falsy (default), we keep
// hitting the Supabase edge functions + RPCs exactly as before.
//
// Flip per environment once the server routes land and the data copy
// completes. See NEEDED_FOR_SERVER.md §Integrations for the server
// contract this path expects, and server/.env.example for the full list
// of related env vars (SYMBOLS_APP_USE_MONGO_INTEGRATIONS, SYMBOLS_API_URL).
//
// Migration status (2026-04-25): server routes are live; data copy in
// progress. Default remains false until the cutover is confirmed stable.
const _env = (key) => {
  try { return (typeof process !== 'undefined' && process.env && process.env[key]) || '' }
  catch { return '' }
}
const _useMongo = () =>
  /^(1|true|yes|on)$/i.test(_env('SYMBOLS_APP_USE_MONGO_INTEGRATIONS'))

// Server URL resolves via @symbo.ls/channels: SYMBOLS_API_URL > channel name
// (NODE_ENV) > defaultChannel ('next').
const _serverBase = () => channelApiUrl().replace(/\/+$/, '')

const _serverFetch = async (path, { method = 'GET', body } = {}) => {
  const token = await _getAccessToken()
  if (!token) return { error: 'not authenticated' }
  try {
    const res = await fetch(`${_serverBase()}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {})
      },
      body: body ? JSON.stringify(body) : undefined
    })
    const json = await res.json().catch(() => null)
    if (!res.ok) return { error: json?.error || `HTTP ${res.status}`, status: res.status }
    return json || { error: 'empty response' }
  } catch (err) {
    return { error: err?.message || 'network error' }
  }
}

// Resolve the governance Supabase URL from the configured client. All
// edge functions live under `${url}/functions/v1/<name>`; we never
// hardcode the URL so env-switched setups (dev vs prod) "just work".
const _externalQueryUrl = () => {
  const client = getClient(PROJECT_GOVERNANCE)
  // supabase-js v2 stashes the URL on the rest builder.
  const url = client?.supabaseUrl || client?.rest?.url || ''
  if (!url) {
    throw new Error('integrations: governance client has no URL — is configure() called?')
  }
  return `${url.replace(/\/+$/, '')}/functions/v1/external-supabase-query`
}

// Active session access token — required for the edge function's JWT
// check. Returns null when the user isn't signed in, so the caller can
// surface a friendly auth error instead of spinning.
const _getAccessToken = async () => {
  const client = getClient(PROJECT_GOVERNANCE)
  if (!client) return null
  const { data } = await client.auth.getSession()
  return data?.session?.access_token || null
}

// Generic op caller. Use the named helpers below for ergonomic signatures.
//
// Under USE_MONGO_INTEGRATIONS, the Supabase-specific ops (select/
// insert/update/delete/rpc/introspect) translate to capability calls
// against the server's `/integrations/call` dispatcher, which owns
// the supabase_project handler. List ops (list_projects, list_integrations)
// don't map to a single integration — they're handled separately.
export const callIntegrationOp = async (body) => {
  if (_useMongo()) {
    const op = body?.op
    if (op === 'list_integrations') {
      const r = await _serverFetch(`/org-integrations?orgId=${encodeURIComponent(body.orgId)}`)
      return r.error ? r : { ok: true, op, result: r.integrations || [] }
    }
    if (op === 'list_projects') {
      const r = await _serverFetch(`/org-integrations?orgId=${encodeURIComponent(body.orgId)}&kind=supabase_project`)
      return r.error ? r : { ok: true, op, result: (r.integrations || []).filter((i) => i.kind === 'supabase_project') }
    }
    // Per-integration ops → capability call. Supabase kinds always.
    const SB_OPS = {
      introspect: 'schema.introspect',
      select: 'data.read',
      insert: 'data.write',
      update: 'data.write',
      delete: 'data.write',
      rpc: 'rpc.call'
    }
    const capability = SB_OPS[op]
    if (!capability) return { error: `unknown op for mongo dispatcher: ${op}` }
    return callCapability(
      { orgId: body.orgId, kind: 'supabase_project', slug: body.slug || 'default' },
      capability,
      { ...body, op: undefined, orgId: undefined, slug: undefined }
    )
  }

  const token = await _getAccessToken()
  if (!token) return { error: 'not authenticated' }

  try {
    const res = await fetch(_externalQueryUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    })
    const json = await res.json().catch(() => null)
    if (!res.ok) {
      return { error: json?.error || `HTTP ${res.status}`, status: res.status }
    }
    return json || { error: 'empty response' }
  } catch (err) {
    return { error: err?.message || 'network error' }
  }
}

// ---------- named helpers ----------

// List all Supabase-project integrations configured for the org.
export const listExternalSupabaseProjects = (orgId) =>
  callIntegrationOp({ op: 'list_projects', orgId })

// List every integration for the org (any kind). Useful for the admin
// hub page to show counts per kind.
export const listOrgIntegrations = (orgId) => {
  if (_useMongo()) {
    return _serverFetch(`/org-integrations?orgId=${encodeURIComponent(orgId)}`)
      .then((r) => r.error ? r : { ok: true, result: r.integrations || [] })
  }
  return callIntegrationOp({ op: 'list_integrations', orgId })
}

// Introspect the schema of a connected external Supabase project.
// Returns { tables: [{ name, columns: [...] }] }.
export const introspectExternalSupabase = (orgId, slug = 'default') =>
  callIntegrationOp({ op: 'introspect', orgId, slug })

// Select rows from a table on a connected external Supabase project.
// filters: [{ column, op, value }]
// order:   { column, ascending }
// Returns { rows, total }.
export const selectExternalSupabase = (orgId, slug, table, opts = {}) =>
  callIntegrationOp({
    op: 'select',
    orgId,
    slug,
    table,
    columns: opts.columns,
    filters: opts.filters,
    order: opts.order,
    limit: opts.limit,
    offset: opts.offset
  })

// Insert one or many rows. `data` can be an object or array of objects.
export const insertExternalSupabase = (orgId, slug, table, data) =>
  callIntegrationOp({ op: 'insert', orgId, slug, table, data })

// Update rows matching filters. `filters` is REQUIRED to avoid
// accidental bulk updates.
export const updateExternalSupabase = (orgId, slug, table, filters, data) =>
  callIntegrationOp({ op: 'update', orgId, slug, table, filters, data })

// Delete rows matching filters. `filters` is REQUIRED.
export const deleteExternalSupabase = (orgId, slug, table, filters) =>
  callIntegrationOp({ op: 'delete', orgId, slug, table, filters })

// Call a PostgreSQL function on the external project via PostgREST RPC.
export const rpcExternalSupabase = (orgId, slug, rpcName, rpcArgs = {}) =>
  callIntegrationOp({ op: 'rpc', orgId, slug, rpc_name: rpcName, rpc_args: rpcArgs })

// ---------- admin ops (integration config CRUD) ----------
//
// These go through the `integration-admin` edge function which
// validates the user JWT, checks admin/owner membership on the target
// org, then performs the vault + org_integrations upsert.
//
// A browser-direct path (user JWT) keeps the system self-contained:
// no dependency on the Symbols server's HMAC proxy layer shipping.
// Admin membership is verified on the edge function side so we can't
// skip it by tampering with the request.

const _adminUrl = () => {
  const client = getClient(PROJECT_GOVERNANCE)
  const url = client?.supabaseUrl || client?.rest?.url || ''
  if (!url) throw new Error('integrations: governance client has no URL')
  return `${url.replace(/\/+$/, '')}/functions/v1/integration-admin`
}

const _adminCall = async (op, args) => {
  const client = getClient(PROJECT_GOVERNANCE)
  if (!client) return { ok: false, body: { error: 'no governance client' } }
  const { data } = await client.auth.getSession()
  const token = data?.session?.access_token
  if (!token) return { ok: false, body: { error: 'not authenticated' } }
  try {
    const res = await fetch(_adminUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ op, ...args })
    })
    const body = await res.json().catch(() => null)
    return { ok: res.ok, body, status: res.status }
  } catch (err) {
    return { ok: false, body: { error: err?.message || 'network error' } }
  }
}

// Upsert an integration instance. Args:
//   orgId, kind, slug?, scopeType?='org', scopeId?=null,
//   displayName?, config?, secret?, enabled?
// scopeType='org' → scopeId must be null (server enforces).
// scopeType='workspace'|'project' → scopeId required.
export const saveIntegration = (args) =>
  _useMongo()
    ? _serverFetch('/org-integrations', { method: 'POST', body: args })
    : _adminCall('upsert', args)

// Delete an integration instance. Must match the row's scope exactly
// (no inheritance on delete). Args: orgId, kind, slug?, scopeType?, scopeId?
export const deleteIntegration = (args) =>
  _useMongo()
    ? _serverFetch('/org-integrations', { method: 'DELETE', body: args })
    : _adminCall('delete', args)

// Marketplace-facing wrapper over saveIntegration. Generates a slug
// from displayName and enforces the scope-null-for-org invariant.
export const addIntegrationFromMarketplace = (args) => {
  if (!args?.kind) return Promise.resolve({ ok: false, body: { error: 'kind required' } })
  const scopeType = args.scopeType || 'org'
  return saveIntegration({
    orgId: args.orgId,
    kind: args.kind,
    slug: slugify(args.displayName || args.config?.display_name || args.kind),
    displayName: args.displayName || args.kind,
    config: args.config || {},
    secret: args.secret,
    scopeType,
    scopeId: scopeType === 'org' ? null : (args.scopeId || null),
    enabled: true
  })
}

// Move an existing row from one scope to another. Args:
//   orgId, kind, slug?,
//   fromScopeType?, fromScopeId?,   -- where the row lives today
//   toScopeType, toScopeId?         -- where it should live
export const assignIntegrationScope = (args) =>
  _useMongo()
    ? _serverFetch('/org-integrations/assign-scope', { method: 'POST', body: args })
    : _adminCall('assign_scope', args)

// §I12 — Persist drag-and-drop instance reorder.
// `slugs` is the new ordered array for the given (orgId, kind, scopeType, scopeId).
// Each slug at index i gets position=i in org_integrations / OrgIntegration.position.
// Args: orgId, kind, slugs, scopeType?='org', scopeId?=null
export const reorderIntegrations = (args) =>
  _useMongo()
    ? _serverFetch('/org-integrations/reorder', { method: 'POST', body: args })
    : _adminCall('reorder', args)

// Read the integration_kinds catalogue + the current org's configured
// integrations. Small helper that batches the two in one RPC round
// trip so the admin hub renders without a waterfall.
export const loadIntegrationCatalogue = async (orgId) => {
  if (_useMongo()) {
    const [kindsRes, integrationsRes] = await Promise.all([
      _serverFetch('/org-integrations/kinds'),
      orgId ? _serverFetch(`/org-integrations?orgId=${encodeURIComponent(orgId)}`) : Promise.resolve({ integrations: [] })
    ])
    return {
      kinds: kindsRes?.kinds || [],
      integrations: integrationsRes?.integrations || []
    }
  }
  const client = getClient(PROJECT_GOVERNANCE)
  if (!client) return { kinds: [], integrations: [] }
  const [kindsRes, integrationsRes] = await Promise.all([
    client.from('integration_kinds').select('*').order('sort_order'),
    orgId ? client.rpc('list_org_integrations', { p_org_id: orgId }) : Promise.resolve({ data: [] })
  ])
  return {
    kinds: kindsRes.data || [],
    integrations: integrationsRes.data || []
  }
}

// ---------- scope-aware unified reader ----------
//
// The "integration units" shape. One entry per configured kind at the
// requested scope (with parent-scope inheritance by default). Built on
// top of `list_org_integrations(p_org_id, p_scope_type, p_scope_id,
// p_include_parents)` which handles the inheritance query server-side.
//
// Shape returned:
//   [{
//     kind, label, category, icon, description,
//     auth_model, default_scope, capabilities, allows_multiple,
//     instances: [
//       { slug, display_name, scope_type, scope_id, config, enabled, has_secret, updated_at }
//     ],
//     count
//   }, …]
//
// Scope descriptor:
//   { type: 'org' | 'workspace' | 'project', orgId, id?, includeParents? }
//
// `id` is required for workspace/project scope. `includeParents` defaults
// to true — a project view merges project + workspace + org rows so
// child contexts inherit org-wide infra (github, google_workspace, etc).

const _fold = (kinds, integrations) => {
  const byKind = {}
  for (const k of kinds || []) {
    byKind[k.kind] = {
      kind: k.kind,
      label: k.label,
      category: k.category,
      icon: k.icon,
      description: k.description,
      auth_model: k.auth_model,
      default_scope: k.default_scope,
      capabilities: k.capabilities || [],
      allows_multiple: k.allows_multiple,
      instances: [],
      count: 0
    }
  }
  for (const i of integrations || []) {
    const unit = byKind[i.kind]
    if (!unit) continue
    unit.instances.push({
      slug: i.slug,
      display_name: i.display_name,
      scope_type: i.scope_type,
      scope_id: i.scope_id,
      config: i.config,
      enabled: i.enabled,
      has_secret: i.has_secret,
      updated_at: i.updated_at
    })
    unit.count += 1
  }
  return Object.values(byKind).sort((a, b) => a.category.localeCompare(b.category))
}

export const loadIntegrationUnits = async (scope = {}) => {
  const { type = 'org', orgId, id = null, includeParents = true } = scope
  if (!orgId) return []
  if ((type === 'workspace' || type === 'project') && !id) {
    throw new Error(`loadIntegrationUnits: scope.id required for type="${type}"`)
  }
  if (_useMongo()) {
    const qs = new URLSearchParams({
      orgId,
      scopeType: type,
      ...(id ? { scopeId: id } : {}),
      includeParents: String(includeParents)
    })
    const [kindsRes, rowsRes] = await Promise.all([
      _serverFetch('/org-integrations/kinds'),
      _serverFetch(`/org-integrations?${qs.toString()}`)
    ])
    return _fold(kindsRes?.kinds || [], rowsRes?.integrations || [])
  }
  const client = getClient(PROJECT_GOVERNANCE)
  if (!client) return []
  const [kindsRes, rowsRes] = await Promise.all([
    client.rpc('list_integration_kinds'),
    client.rpc('list_org_integrations', {
      p_org_id: orgId,
      p_scope_type: type,
      p_scope_id: id,
      p_include_parents: includeParents
    })
  ])
  return _fold(kindsRes.data || [], rowsRes.data || [])
}

// Convenience wrappers — pick whichever is most natural at the call site.
export const loadOrgIntegrations = (orgId, opts = {}) =>
  loadIntegrationUnits({ type: 'org', orgId, ...opts })

export const loadWorkspaceIntegrations = (orgId, workspaceId, opts = {}) =>
  loadIntegrationUnits({ type: 'workspace', orgId, id: workspaceId, ...opts })

export const loadProjectIntegrations = (orgId, projectId, opts = {}) =>
  loadIntegrationUnits({ type: 'project', orgId, id: projectId, ...opts })

// Catalogue-only read for UI that renders the "what could you connect?"
// list without needing an org context (onboarding, marketing).
export const listIntegrationKinds = async () => {
  if (_useMongo()) {
    const res = await _serverFetch('/org-integrations/kinds')
    return res?.kinds || []
  }
  const client = getClient(PROJECT_GOVERNANCE)
  if (!client) return []
  const res = await client.rpc('list_integration_kinds')
  return res.data || []
}

// Capability discovery — return all kinds that declare a capability.
// Useful when the canvas asks "which integrations can send webhooks?"
export const findIntegrationsByCapability = async (capability, scope) => {
  const units = await loadIntegrationUnits(scope)
  return units.filter((u) => (u.capabilities || []).includes(capability))
}

// ---------- capability execution ----------
//
// Generic verb dispatcher. The edge function `integration-call` looks
// up the integration row by (orgId, scope, kind, slug), verifies the
// caller's org membership, and routes to a per-kind handler. The
// capability string ('webhook.trigger', 'http.request', 'metric.query',
// …) tells the handler *what* to do; `args` is opaque payload.
//
// For data ops on a supabase_project, keep using the dedicated helpers
// (selectExternalSupabase, etc.) — they're thinner and typed. Use this
// for n8n, custom_http, google_analytics, and future kinds.
//
// ref shape: { orgId, kind, slug?='default', scopeType?='org', scopeId?=null }

const _callUrl = () => {
  const client = getClient(PROJECT_GOVERNANCE)
  const url = client?.supabaseUrl || client?.rest?.url || ''
  if (!url) throw new Error('sdk-bridge: governance client has no URL')
  return `${url.replace(/\/+$/, '')}/functions/v1/integration-call`
}

export const callCapability = async (ref, capability, args = {}) => {
  const body = {
    orgId: ref.orgId,
    kind: ref.kind,
    slug: ref.slug || 'default',
    scopeType: ref.scopeType || 'org',
    scopeId: ref.scopeId ?? null,
    capability,
    args
  }
  if (_useMongo()) {
    return _serverFetch('/org-integrations/call', { method: 'POST', body })
  }
  const token = await _getAccessToken()
  if (!token) return { error: 'not authenticated' }
  try {
    const res = await fetch(_callUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    })
    const json = await res.json().catch(() => null)
    if (!res.ok) return { error: json?.error || `HTTP ${res.status}`, status: res.status }
    return json || { error: 'empty response' }
  } catch (err) {
    return { error: err?.message || 'network error' }
  }
}
